/* eslint-disable react/prop-types */
/**
 * TyreReplacementGroupCard
 * ========================
 * Iter187/189 — FOH Pricing card for the multi-tyre structured repair
 * group with always-visible labour/parts/charges breakdown.
 *
 * Layout (top-to-bottom, single visible card — no nested expand):
 *   1. Header — title, integrity badge, severity summary line
 *   2. Per-tyre child rows
 *   3. Breakdown table — Labour / Parts (per tyre) / Included items
 *      (balancing, valves, disposal) — inline editable
 *   4. Recommendations — Add Wheel Alignment (direct POST), axle pair
 *   5. Pricing footer
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import {
  AlertTriangle, Wrench, CheckCircle2, MoveRight, Layers,
  Loader2, Pencil, Lock, Plus,
} from "lucide-react";
import CAESection from "@/components/pricing/CAESection";

const SEVERITY_STYLES = {
  dangerous:   "bg-red-600 text-white",
  major:       "bg-red-500 text-white",
  damaged:     "bg-orange-600 text-white",
  puncture:    "bg-orange-500 text-white",
  advisory:    "bg-amber-500 text-white",
  worn:        "bg-amber-400 text-amber-900",
  uneven_wear: "bg-amber-300 text-amber-900",
  age_related: "bg-slate-400 text-white",
};

const SEVERITY_LABELS = {
  dangerous: "Dangerous", major: "Major", damaged: "Damaged",
  puncture: "Puncture", advisory: "Advisory", worn: "Worn",
  uneven_wear: "Uneven Wear", age_related: "Age-Related",
};

const INTEGRITY_STYLES = {
  required:    { wrap: "border-red-300 bg-red-50/60",     badge: "bg-red-700 text-white",   label: "Required" },
  recommended: { wrap: "border-amber-300 bg-amber-50/60", badge: "bg-amber-700 text-white", label: "Recommended" },
  advisory:    { wrap: "border-slate-300 bg-slate-50/60", badge: "bg-slate-700 text-white", label: "Advisory" },
};

function PriceCell({ value, onChange, onBlur, testId, prefix = "£" }) {
  // iter196 — Empty-state UX. Show a blank input when the value is
  // 0/null/undefined so FOH doesn't have to delete a placeholder "0"
  // before typing. The HTML `number` input previously rendered the
  // literal "0", leading to "020.95" when FOH typed past it.
  const numericValue = parseFloat(value);
  const display = Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : "";
  return (
    <div className="flex items-center gap-1 text-[12.5px]">
      <span className="text-slate-500">{prefix}</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={display}
        placeholder="0.00"
        onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
        onBlur={onBlur}
        className="w-20 px-1 py-0.5 border border-slate-300 rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-400"
        data-testid={testId}
      />
    </div>
  );
}

export default function TyreReplacementGroupCard({
  jobGroup,
  yestimateId,
  onRefresh,
  onAddAxlePair,
  onConfirmPricing,
  onUnconfirmPricing,
  onLocalChange,
}) {
  const [savingAlignment, setSavingAlignment] = useState(false);
  const [editing,         setEditing] = useState(false);
  const [localLabour,     setLocalLabour]  = useState(jobGroup.labour_lines || []);
  const [localParts,      setLocalParts]   = useState(jobGroup.parts_lines  || []);
  const [localCharges,    setLocalCharges] = useState(jobGroup.charges      || []);
  // iter191 — per-tyre spec state. Mirrors `parts_lines[i].tyre_*`
  // back onto the `tyres[i]` child so the customer portal renders
  // the latest FOH-quoted brand, not the original MCC capture.
  const [localTyres,      setLocalTyres]   = useState(jobGroup.tyres        || []);
  // Feb 2026 — Add-another-tyre + promote-advisory state. Tracks
  // which positions are busy being added so the UI can show a
  // spinner / disable duplicate clicks. `addPickerOpen` toggles the
  // FOH position picker for the "+ Add another tyre" CTA.
  const [busyPositions, setBusyPositions] = useState({});
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  // iter193b — Raw brand+model string per parts row. Decoupled from
  // the parsed tyre_brand/tyre_model fields so the user can type
  // "Michelin Primacy 4" character-by-character including the spaces
  // between brand and model without the on-change parse/rejoin
  // stripping trailing whitespace. We parse tokens only on blur,
  // when the value is final.
  const [brandRaw, setBrandRaw] = useState(() => {
    const init = {};
    (jobGroup.parts_lines || []).forEach((p, i) => {
      const key = p.id || `pt-${i}`;
      if (p.tyre_brand || p.tyre_model) {
        init[key] = [p.tyre_brand, p.tyre_model].filter(Boolean).join(" ");
      } else if (p.tyre_spec) {
        const sz = p.tyre_size || "";
        const spec = String(p.tyre_spec).trim();
        init[key] = sz && spec.toLowerCase().startsWith(sz.toLowerCase())
          ? spec.slice(sz.length).trim()
          : spec;
      } else {
        init[key] = "";
      }
    });
    return init;
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  // iter193 (Feb 2026) — Track which parts_lines came in MCC-locked.
  // iter197c (Jun 2026) — Predicate refined: only real tyre sizes
  //   (containing a digit/digit segment like "205/55") count as
  //   locked, so single-char garbage doesn't lock the input.
  // iter197d (Jun 2026) — Detection MUST anchor to the *first* mount
  //   of this jobGroup ONLY. Previously the memo recomputed every
  //   time parts_lines changed reference (i.e. every auto-save),
  //   which meant that as soon as the user typed "225/6" (a string
  //   that now matches the real-tyre predicate), the save+resync
  //   flipped the input to a read-only chip mid-typing. We now lock
  //   the detection to `jobGroup.id` and extend the map lazily when
  //   new rows are added (e.g. "+ Add another tyre").
  const _isRealTyreSize = (v) => /\d+\s*\/\s*\d+/.test(String(v || ""));
  const originalMccCapturedRef = useRef(null);
  if (
    originalMccCapturedRef.current === null
    || originalMccCapturedRef.current._jg_id !== jobGroup.id
  ) {
    // iter198 — Also capture the ORIGINAL MCC/VI size value per row
    // so FOH can one-tap "Reset to MCC value" if they mistype their
    // override. Without this, any overwritten size is gone forever.
    //
    // iter198g (Feb 2026) — Prefer `p.mcc_captured_size` (a fresh
    // read-time stamp from the backend `enrich_tyre_group_from_mcc`
    // pass) over the mount-time `p.tyre_size`. The backend stamp
    // reflects the LATEST tester capture even when FOH has
    // overridden the canonical size — so the "tester" pill below
    // always tells the truth about what the tester recorded at the
    // ramp.
    const map = { _jg_id: jobGroup.id, size: [], brand: [], sizeValues: [] };
    (jobGroup.parts_lines || []).forEach((p) => {
      const real = _isRealTyreSize(p.tyre_size);
      map.size.push(real);
      map.brand.push(!!(p.tyre_brand && String(p.tyre_brand).trim()));
      // Tester capture is sourced from the backend's MCC enrichment
      // when present; otherwise we fall back to the mount-time
      // tyre_size (legacy behaviour for yestimates that pre-date the
      // enrichment endpoint).
      const mccSize = (p.mcc_captured_size && String(p.mcc_captured_size).trim()) || "";
      if (mccSize) {
        map.sizeValues.push(mccSize);
      } else {
        map.sizeValues.push(real ? String(p.tyre_size) : "");
      }
    });
    originalMccCapturedRef.current = map;
  } else {
    // Same JG, but parts_lines may have grown (FOH added a tyre).
    // Pad the lock map with `false` for the new positions so they
    // start out editable.
    const currentLen = (jobGroup.parts_lines || []).length;
    while (originalMccCapturedRef.current.size.length < currentLen) {
      originalMccCapturedRef.current.size.push(false);
      originalMccCapturedRef.current.brand.push(false);
      originalMccCapturedRef.current.sizeValues.push("");
    }
  }
  const originalMccCaptured = originalMccCapturedRef.current;
  /* eslint-enable react-hooks/exhaustive-deps */

  /* eslint-disable react-hooks/exhaustive-deps */
  // iter196 — Bubble live local edits up to the parent so the
  // page-level grand total reflects what FOH just typed (before
  // they hit "Confirm Pricing Complete"). Without this, the
  // bottom-bar Subtotal/VAT stayed at the £0-tyre baseline until
  // save. Throttled via useEffect to one event per change cycle.
  useEffect(() => {
    if (!onLocalChange) return;
    onLocalChange(jobGroup.id, {
      labour_lines: localLabour,
      parts_lines:  localParts,
      charges:      localCharges,
      tyres:        localTyres,
    });
  }, [localParts, localCharges, localLabour, localTyres]);
  /* eslint-enable react-hooks/exhaustive-deps */

  /* eslint-disable react-hooks/exhaustive-deps */
  // iter193d (Feb 2026) — Re-sync local state when the parent passes
  // a fresh ``jobGroup`` (e.g. after onRefresh fires post-Save or
  // after a tyre-children upsert). Without this, localParts/Tyres/
  // Labour/Charges are initialised once at mount and never reflect
  // a child added via the "+ Include" toggle or "+ Add another
  // tyre" picker — the pricing panel stays stale until the user
  // navigates away and back.
  //
  // Anchored to ``jobGroup.updated_at`` (server stamps this on every
  // change) so we don't fight the user's in-progress edits when the
  // parent re-renders without a true upstream change.
  useEffect(() => {
    setLocalLabour(jobGroup.labour_lines || []);
    setLocalParts(jobGroup.parts_lines  || []);
    setLocalCharges(jobGroup.charges     || []);
    setLocalTyres(jobGroup.tyres         || []);
    setBrandRaw(() => {
      const next = {};
      (jobGroup.parts_lines || []).forEach((p, i) => {
        const key = p.id || `pt-${i}`;
        if (p.tyre_brand || p.tyre_model) {
          next[key] = [p.tyre_brand, p.tyre_model].filter(Boolean).join(" ");
        } else {
          next[key] = "";
        }
      });
      return next;
    });
  }, [jobGroup.id, jobGroup.updated_at, jobGroup.tyres?.length, jobGroup.parts_lines?.length]);
  /* eslint-enable react-hooks/exhaustive-deps */

  /**
   * iter195 (Feb 2026) — Per-tyre price propagation.
   * When FOH enters a buy-in or retail on one tyre, broadcast it to
   * other tyres that don't yet have a price set. Saves repetitive
   * data entry when all 4 corners are the same brand/size at the
   * same price. Skip rule: never overwrite a non-zero value already
   * entered by FOH on a sibling.
   */
  const propagatePriceOnBlur = (sourceIdx, field) => {
    setLocalParts((prev) => {
      const source = prev[sourceIdx];
      if (!source) return prev;
      const newVal = parseFloat(source[field]) || 0;
      if (newVal <= 0) return prev; // no value yet
      return prev.map((row, j) => {
        if (j === sourceIdx) return row;
        const existing = parseFloat(row[field]) || 0;
        if (existing > 0) return row; // FOH typed on sibling — preserve
        return { ...row, [field]: newVal };
      });
    });
  };

  /**
   * iter193b (Feb 2026) — FOH pricing-side tyre spec propagation.
   *
   * Propagates a finalised size or brand value from one tyre row to
   * its siblings. Fires on **blur**, not on every keystroke — the
   * per-keystroke approach truncated the propagated value because
   * the never-overwrite guard saw the partial first write and then
   * blocked all subsequent updates from the same source.
   *
   * iter198 (Feb 2026) — Size edits now ALWAYS overwrite sibling
   * sizes by default. Real-world workshops fit the same size across
   * all four corners ~95% of the time, and when FOH explicitly edits
   * the size column they're declaring the canonical size for the
   * job — staggered fits are the rare exception and FOH can re-edit
   * any sibling row afterwards. Brand propagation keeps the
   * fill-empty-only semantics so mixed brands across axles survive.
   *
   * Skip-rule (only the source row is skipped):
   *   • Sibling at sourceIdx — the row that triggered the propagation.
   *
   * Also mirrors the value onto the matching `localTyres` child so
   * the customer portal (driven off `tyres[i].tyre_size/brand/model`)
   * reflects the FOH-quoted spec, not the original MCC capture.
   */
  const propagateOnBlur = (sourceIdx, field, value, extraFields = {}) => {
    if (!value) return;
    if (field !== "tyre_size" && field !== "tyre_brand") return;
    setLocalParts((prev) => {
      const next = [...prev];
      next.forEach((row, j) => {
        if (j === sourceIdx) return;
        if (field === "tyre_size") {
          // Always-overwrite for size (the canonical job-level field).
          next[j] = { ...row, tyre_size: value };
          return;
        }
        // Brand keeps the fill-empty-only semantics — staggered
        // brand fitments are common enough that we don't want to
        // clobber FOH's per-position choices.
        const wasMccLocked = !!originalMccCaptured.brand[j];
        if (wasMccLocked) return;
        const existing = (row[field] || "").toString().trim();
        if (existing) return; // FOH typed an override on this sibling
        next[j] = { ...row, [field]: value, ...extraFields };
      });
      return next;
    });
    // Mirror brand → also update brandRaw for any siblings that got
    // filled (so the controlled brand input renders the propagated
    // value).
    if (field === "tyre_brand") {
      setBrandRaw((prev) => {
        const next = { ...prev };
        (localParts || []).forEach((row, j) => {
          if (j === sourceIdx) return;
          if (originalMccCaptured.brand[j]) return;
          if ((row.tyre_brand || "").toString().trim()) return;
          const key = row.id || `pt-${j}`;
          if ((next[key] || "").toString().trim()) return;
          // Propagated brand can include "Brand Model" — pass the
          // whole raw string the source row used.
          next[key] = (extraFields._raw || value);
        });
        return next;
      });
    }
    // Mirror onto matching tyre children for the customer portal.
    setLocalTyres((prev) =>
      prev.map((t) => {
        if (field === "tyre_size") {
          // Size always overwrites on children too — keeps the
          // portal in lockstep with the FOH-quoted size.
          return { ...t, tyre_size: value };
        }
        const wasLocked = (t[field] || "").toString().trim();
        if (wasLocked) return t;
        if (field === "tyre_brand") {
          return {
            ...t,
            tyre_brand: value,
            ...(extraFields.tyre_model !== undefined
              ? { tyre_model: extraFields.tyre_model }
              : {}),
          };
        }
        return { ...t, [field]: value };
      }),
    );
  };

  if (!jobGroup || jobGroup.subtype !== "tyre_replacement_group") return null;
  const tyres = jobGroup.tyres || [];
  const integrity = INTEGRITY_STYLES[jobGroup.integrity_tier] || INTEGRITY_STYLES.recommended;
  const portal = jobGroup.portal_summary || {};
  const alignmentSuggest = jobGroup.alignment_soft_suggest;
  const axlePair = jobGroup.axle_pair_recommendation;
  const groupedLabour = jobGroup.grouped_labour || {};
  const counts = {
    dangerous: tyres.filter((t) => t.severity === "dangerous").length,
    advisory:  tyres.filter((t) => ["advisory", "worn", "uneven_wear"].includes(t.severity)).length,
  };

  // iter189 — Direct-add Wheel Alignment. POST to the alignment
  // endpoint with the engine's already-computed trigger reasons +
  // recommended alignment_type. No modal handoff: the rec lands in
  // pricing as a separate priced JG immediately, parent refreshes.
  const handleAddAlignment = async () => {
    if (!alignmentSuggest || !yestimateId) return;
    try {
      setSavingAlignment(true);
      const payload = {
        alignment_type:        alignmentSuggest.recommended_alignment_type || "four_wheel",
        trigger_reasons:       alignmentSuggest.trigger_reasons || [],
        linked_repair_jg_ids:  [jobGroup.id],
        custom_note:           "Auto-added from tyre group recommendation.",
      };
      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/recommendations/alignment`,
        payload,
      );
      toast.success("Wheel alignment added as a separate pricing item.");
      onRefresh?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to add alignment recommendation.",
      );
    } finally {
      setSavingAlignment(false);
    }
  };

  /**
   * Feb 2026 (LJ65 WLC) — FOH-driven tyre add / promote.
   *
   * One endpoint, two use cases:
   *   • Promote an existing advisory child into the priced parts table
   *     (toggle replacement_required true).
   *   • Add a brand-new tyre for a position that wasn't on the MOT
   *     so FOH can sell a full axle / all-corner replacement.
   */
  const upsertTyreChild = async (position, opts = {}) => {
    if (!yestimateId || !jobGroup.id || !position) return;
    setBusyPositions((p) => ({ ...p, [position]: true }));
    try {
      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/job-group/${jobGroup.id}/tyre-children`,
        {
          position,
          severity: opts.severity || "advisory",
          replacement_required:
            opts.replacement_required !== undefined ? opts.replacement_required : true,
          reason: opts.reason || null,
        },
      );
      toast.success(
        opts.replacement_required === false
          ? `${position} removed from pricing.`
          : `${position} ${opts.isPromote ? "added to pricing" : "added"}.`,
      );
      setAddPickerOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tyre update failed.");
    } finally {
      setBusyPositions((p) => {
        const n = { ...p };
        delete n[position];
        return n;
      });
    }
  };

  // Positions a 4th tyre can take = the 4 corners minus the ones
  // already present in this group.
  const presentPositions = new Set(
    tyres.map((t) => (t.position || "").toUpperCase()).filter(Boolean),
  );
  const addablePositions = ["NSF", "OSF", "NSR", "OSR"].filter(
    (p) => !presentPositions.has(p),
  );

  return (
    <section
      className={`rounded-lg border-2 ${integrity.wrap} mb-4 overflow-hidden`}
      data-testid="tyre-replacement-group-card"
      data-tyre-count={tyres.length}
      data-integrity-tier={jobGroup.integrity_tier}
    >
      {/* Header */}
      <header className="px-4 py-3 flex items-start gap-3 border-b border-slate-200 bg-white/50">
        <div className="p-1.5 rounded bg-slate-900 text-white shrink-0">
          <Layers className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-[14px] font-bold text-slate-900 leading-tight">
              {jobGroup.title || "Tyre Replacement Group"}
            </h3>
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${integrity.badge}`}
              data-testid="tyre-group-integrity-badge"
            >
              Integrity: {integrity.label}
            </span>
          </div>
          <p className="text-[12.5px] text-slate-700 leading-snug mt-1">
            {counts.dangerous > 0 && (
              <span className="mr-2"><span className="font-bold text-red-700">{counts.dangerous} dangerous</span></span>
            )}
            {counts.advisory > 0 && (
              <span className="mr-2"><span className="font-semibold text-amber-700">{counts.advisory} advisory / worn</span></span>
            )}
            {alignmentSuggest?.warranted && (
              <span className="mr-2"><span className="font-semibold text-blue-700">1 alignment consideration</span></span>
            )}
            {axlePair && (
              <span className="mr-2"><span className="font-semibold text-slate-700">1 axle-pair suggestion</span></span>
            )}
          </p>
        </div>
      </header>

      {/* Per-tyre rows */}
      <div className="px-4 py-2.5 bg-white/30">
        <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
          Tyres in this group
        </p>
        <ul className="space-y-1" data-testid="tyre-group-children-list">
          {tyres.map((t) => {
            const pos = (t.position || "").toUpperCase();
            const isBusy = !!busyPositions[pos];
            const replacing = !!t.replacement_required;
            const addedByFoh = t.added_by === "foh";
            return (
              <li
                key={t.id || t.position}
                className="flex items-start gap-2 py-1 text-[12.5px]"
                data-testid={`tyre-child-${t.position || "x"}`}
                data-severity={t.severity}
                data-replacing={replacing ? "1" : "0"}
              >
                <span className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  addedByFoh
                    ? "bg-blue-600 text-white"
                    : (SEVERITY_STYLES[t.severity] || "bg-slate-300 text-slate-900")
                }`}>
                  {addedByFoh
                    ? "FOH-ADDED"
                    : (SEVERITY_LABELS[t.severity] || t.severity)}
                </span>
                <span className="font-semibold text-slate-900 shrink-0">
                  {t.position_label || t.position}
                </span>
                <span className="text-slate-700 leading-snug flex-1">
                  — {t.issue}
                </span>
                {t.wear_pattern && (
                  <span className="text-[10.5px] text-slate-500 italic shrink-0">
                    Pattern: {t.wear_pattern.replace(/_/g, " ")}
                  </span>
                )}
                {/* Feb 2026 — Include / exclude from pricing toggle.
                    Lets FOH promote an advisory MOT tyre into the priced
                    parts table, or back the customer out of an upsold
                    tyre, without rebuilding the entire group. */}
                {yestimateId && pos && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => {
                      const newVal = !replacing;
                      upsertTyreChild(pos, {
                        severity: t.severity || "advisory",
                        replacement_required: newVal,
                        isPromote: newVal,
                        reason: newVal
                          ? "FOH included in customer quote"
                          : "FOH removed from customer quote",
                      });
                    }}
                    className={`shrink-0 text-[10.5px] font-semibold rounded border px-2 py-0.5 transition-colors ${
                      replacing
                        ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                        : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-slate-400"
                    } disabled:opacity-60`}
                    data-testid={`tyre-child-include-toggle-${pos}`}
                    title={
                      replacing
                        ? "Tap to remove this tyre from the priced quote"
                        : "Tap to include this tyre in the priced quote"
                    }
                  >
                    {isBusy ? (
                      <Loader2 className="w-3 h-3 animate-spin inline" />
                    ) : replacing ? (
                      <><CheckCircle2 className="w-3 h-3 inline mr-0.5" />In quote</>
                    ) : (
                      <>+ Include</>
                    )}
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        {/* Feb 2026 — Add another tyre CTA. Opens a small picker
            showing only the corners not already in the group, so FOH
            can sell a full axle / all-corner replacement (e.g. 2 MOT
            failures + 1 advisory → propose pricing all 4 to the
            customer). When all 4 corners are present, the button is
            replaced with an explainer. */}
        {yestimateId && (
          <div className="mt-2 pt-2 border-t border-dashed border-slate-200">
            {addablePositions.length === 0 ? (
              <p
                className="text-[11px] text-slate-500 italic"
                data-testid="tyre-group-add-all-corners-present"
              >
                All four corners are already in this group.
              </p>
            ) : !addPickerOpen ? (
              <button
                type="button"
                onClick={() => setAddPickerOpen(true)}
                className="w-full text-left rounded border-2 border-dashed border-blue-300 bg-blue-50/40 px-3 py-2 text-[12.5px] font-semibold text-blue-800 hover:bg-blue-50 hover:border-blue-400 flex items-center justify-center gap-1.5"
                data-testid="tyre-group-add-another-tyre-cta"
              >
                <Plus className="w-4 h-4" />
                Add another tyre to this group
              </button>
            ) : (
              <div
                className="rounded border-2 border-blue-300 bg-blue-50/60 px-3 py-2.5"
                data-testid="tyre-group-add-another-tyre-picker"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-800">
                    Pick a position to add
                  </p>
                  <button
                    type="button"
                    onClick={() => setAddPickerOpen(false)}
                    className="text-[11px] text-slate-500 hover:text-slate-800 underline"
                    data-testid="tyre-group-add-another-tyre-cancel"
                  >
                    Cancel
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {addablePositions.map((p) => {
                    const isBusy = !!busyPositions[p];
                    return (
                      <button
                        key={p}
                        type="button"
                        disabled={isBusy}
                        onClick={() => upsertTyreChild(p, {
                          severity: "advisory",
                          replacement_required: true,
                          reason: "FOH-added tyre — pre-emptive replacement for safety/longevity",
                        })}
                        className="rounded border border-slate-300 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-800 hover:bg-blue-50 hover:border-blue-400 disabled:opacity-60 flex items-center justify-center gap-1.5"
                        data-testid={`tyre-group-add-pos-${p}`}
                      >
                        {isBusy ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Plus className="w-3.5 h-3.5" />
                        )}
                        {p}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10.5px] text-slate-600 italic mt-1.5 leading-snug">
                  Added tyres land in the priced parts table immediately so the customer sees a full all-corner quote.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Always-visible Breakdown */}
      <div
        className="px-4 py-2.5 border-t border-slate-200 bg-white/60"
        data-testid="tyre-group-breakdown"
      >
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10.5px] uppercase tracking-wider text-slate-600 font-semibold">
            Pricing Breakdown
          </p>
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="text-[11px] inline-flex items-center gap-1 text-blue-700 hover:text-blue-900"
            data-testid="tyre-group-edit-toggle"
          >
            <Pencil className="w-3 h-3" /> {editing ? "Done editing" : "Edit lines"}
          </button>
        </div>
        {/* Labour — Feb 2026: tyre fitting labour is now rolled into
            the sell-out tyre price per product owner, so the BE
            returns an empty labour_lines array. We hide the section
            entirely when there are no labour lines. */}
        {localLabour.length > 0 && (
          <table className="w-full text-[12px] mb-1.5" data-testid="tyre-group-labour-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left font-semibold py-1">Labour</th>
                <th className="text-right font-semibold py-1 w-16">Hrs</th>
                <th className="text-right font-semibold py-1 w-20">Rate</th>
                <th className="text-right font-semibold py-1 w-20">Total</th>
              </tr>
            </thead>
            <tbody>
              {localLabour.map((l, i) => (
                <tr key={l.id || `lab-${i}`} className="border-t border-slate-100">
                  <td className="py-1 text-slate-800">{l.description || "Tyre fitting (grouped)"}</td>
                  <td className="text-right py-1">
                    {editing ? (
                      <PriceCell value={l.hours} onChange={(v) => {
                        const next = [...localLabour];
                        next[i] = { ...l, hours: v };
                        setLocalLabour(next);
                      }} testId={`labour-hours-${i}`} prefix="" />
                    ) : (
                      <span className="text-slate-800">{(l.hours ?? 0).toFixed(2)}h</span>
                    )}
                  </td>
                  <td className="text-right py-1">
                    {editing ? (
                      <PriceCell value={l.rate} onChange={(v) => {
                        const next = [...localLabour];
                        next[i] = { ...l, rate: v };
                        setLocalLabour(next);
                      }} testId={`labour-rate-${i}`} />
                    ) : (
                      <span className="text-slate-800">£{(l.rate ?? 0).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="text-right py-1 font-semibold text-slate-900">
                    £{((l.hours ?? 0) * (l.rate ?? 0)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Parts (per tyre) — iter191b: split spec into Size (locked,
            auto-filled from MCC/VI tester capture) + Brand / Model
            (FOH-editable). Full pricing parity columns (Buy-in /
            Retail / Margin% / Total) match every other part row. */}
        <table className="w-full text-[12px] mb-1.5" data-testid="tyre-group-parts-table">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left font-semibold py-1">Parts — Tyres</th>
              <th className="text-left font-semibold py-1 w-52">Size <span className="text-slate-400 normal-case font-normal">(tester)</span></th>
              <th className="text-left font-semibold py-1">Brand / model</th>
              <th className="text-right font-semibold py-1 w-10">Qty</th>
              <th className="text-right font-semibold py-1 w-20">Buy-in</th>
              <th className="text-right font-semibold py-1 w-20">Retail</th>
              <th className="text-right font-semibold py-1 w-14">Margin</th>
              <th className="text-right font-semibold py-1 w-20">Total</th>
            </tr>
          </thead>
          <tbody>
            {localParts.map((p, i) => {
              const buyIn = parseFloat(p.buy_in_price) || 0;
              const retail = parseFloat(p.unit_price) || 0;
              const qty = parseFloat(p.quantity ?? 1) || 1;
              const marginPct = retail > 0 ? ((retail - buyIn) / retail) * 100 : 0;
              const total = qty * retail;
              const sizeValue = p.tyre_size || "";
              // iter193 — was-this-MCC-locked check anchored to initial
              // mount, not live state.
              // iter198 (Feb 2026) — Also require the CURRENT value to
              // look like a real tyre size (kept for backward compat;
              // referenced by `propagateOnBlur` and `originalMccCaptured`
              // construction).
              // iter198d (Feb 2026) — Renderer no longer branches on
              // a locked-badge / input toggle; every row uses a single
              // always-editable input. The flags below are retained
              // for the propagation skip rules.
              // eslint-disable-next-line no-unused-vars
              const isMccSizeLocked = !!originalMccCaptured.size[i]
                && _isRealTyreSize(sizeValue);
              // Reconstruct brand+model — prefer explicit fields, fall
              // back to parsing what was previously typed into tyre_spec
              // (older yestimates that pre-date the split).
              const brandModelValue = (() => {
                if (p.tyre_brand || p.tyre_model) {
                  return [p.tyre_brand, p.tyre_model].filter(Boolean).join(" ");
                }
                const spec = (p.tyre_spec || "").trim();
                if (!spec) return "";
                if (sizeValue && spec.toLowerCase().startsWith(sizeValue.toLowerCase())) {
                  return spec.slice(sizeValue.length).trim();
                }
                return spec;
              })();
              const baseLabel = (() => {
                const m = (p.description || "").match(/^Tyre\s+—\s+([^—(]+?)(?:\s+—|\s+\(|$)/);
                return m ? `Tyre — ${m[1].trim()}` : (p.description || "Tyre");
              })();
              return (
                <tr key={p.id || `pt-${i}`} className="border-t border-slate-100 align-top">
                  <td className="py-1 text-slate-800">{baseLabel}</td>
                  <td className="py-1">
                    {/* iter198d (Feb 2026) — Always-editable size input.
                        Replaces the lock-badge / input toggle that was
                        confusing FOH ("propagation isn't working", "I
                        can't see the tester capture"). Now every row
                        is a normal input pre-filled with the MCC-
                        captured size. A small grey "tester" pill
                        appears when the live value matches the
                        original capture so FOH can see at a glance
                        where the value came from. Editing any row
                        and tabbing out copies the new size to all
                        siblings with a toast confirming what
                        happened — so the propagation is *visible*
                        even when the other rows already showed the
                        same value. */}
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={sizeValue}
                          onChange={(e) => {
                            const newVal = e.target.value;
                            setLocalParts((prev) => {
                              const next = [...prev];
                              next[i] = { ...next[i], tyre_size: newVal };
                              return next;
                            });
                            if (p.tyre_position || p.tyre_child_id) {
                              setLocalTyres((prev) => prev.map((t) => {
                                const matchByPos = p.tyre_position
                                  && (t.position || "").toLowerCase()
                                     === p.tyre_position.toLowerCase();
                                const matchById = p.tyre_child_id && t.id === p.tyre_child_id;
                                if (!matchByPos && !matchById) return t;
                                return { ...t, tyre_size: newVal };
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const finalVal = (e.target.value || "").trim();
                            if (!finalVal) return;
                            // Only propagate when (a) the value
                            // changed and (b) it looks like a real
                            // size — avoids broadcasting partial
                            // typing if the field is tabbed out
                            // mid-edit.
                            if (!_isRealTyreSize(finalVal)) return;
                            // Count siblings that will actually be
                            // overwritten to a *different* value so
                            // the toast only fires when something
                            // visibly changes.
                            const willChange = (localParts || []).filter((row, j) => (
                              j !== i && (row.tyre_size || "") !== finalVal
                            )).length;
                            propagateOnBlur(i, "tyre_size", finalVal);
                            if (willChange > 0) {
                              toast.success(
                                `Tyre size copied to ${willChange} other tyre${willChange > 1 ? "s" : ""} (${finalVal})`,
                                { duration: 2500 },
                              );
                            }
                          }}
                          placeholder="e.g. 205/55 R16"
                          className={`w-full px-1.5 py-0.5 border rounded text-[11.5px] font-mono focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                            originalMccCaptured.sizeValues[i] && originalMccCaptured.sizeValues[i] === sizeValue
                              ? "border-slate-300 bg-slate-50 text-slate-700"
                              : "border-blue-300 bg-blue-50 text-blue-900"
                          }`}
                          data-testid={`tyre-part-size-input-${p.tyre_position || i}`}
                          title="Type the tyre size — it copies to the other tyres automatically when you tab out. Override an individual row after if you're fitting a staggered set."
                        />
                        {/* Tester pill — shows when value matches the
                            tester's MCC/VI capture. Disappears as
                            soon as FOH edits to make the override
                            state crystal clear. */}
                        {originalMccCaptured.sizeValues[i]
                          && originalMccCaptured.sizeValues[i] === sizeValue
                          && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-slate-200 text-slate-600 text-[9.5px] font-semibold uppercase tracking-wide shrink-0"
                              title="This value was captured by the tester at MCC / VI"
                              data-testid={`tyre-part-size-tester-${p.tyre_position || i}`}
                            >
                              <Lock className="w-2.5 h-2.5" />
                              tester
                            </span>
                          )}
                      </div>
                      {/* Reset to tester value — surfaces when the
                          live value differs from the original
                          capture. One tap restores the original AND
                          propagates back to all four corners. */}
                      {originalMccCaptured.sizeValues[i]
                        && originalMccCaptured.sizeValues[i] !== sizeValue
                        && (
                          <button
                            type="button"
                            className="self-start text-[10.5px] text-blue-600 hover:text-blue-800 hover:underline px-0.5"
                            onClick={() => {
                              const orig = originalMccCaptured.sizeValues[i];
                              // iter198h (Feb 2026) — Reset is a
                              // PER-ROW action. Earlier revisions
                              // propagated the reset to siblings,
                              // which flipped the other tyres back
                              // to "FOH-edited" styling and reset
                              // each of their own tester-value links
                              // (each corner has its own MCC
                              // capture). FOH expects the reset to
                              // affect only the corner they clicked.
                              setLocalParts((prev) => {
                                const next = [...prev];
                                next[i] = { ...next[i], tyre_size: orig };
                                return next;
                              });
                              if (p.tyre_position || p.tyre_child_id) {
                                setLocalTyres((prev) => prev.map((t) => {
                                  const matchByPos = p.tyre_position
                                    && (t.position || "").toLowerCase()
                                       === p.tyre_position.toLowerCase();
                                  const matchById = p.tyre_child_id && t.id === p.tyre_child_id;
                                  if (!matchByPos && !matchById) return t;
                                  return { ...t, tyre_size: orig };
                                }));
                              }
                              toast.success(`Reset to tester value (${orig})`, { duration: 2000 });
                            }}
                            data-testid={`tyre-part-size-reset-${p.tyre_position || i}`}
                            title={`Restore the size captured by the tester (${originalMccCaptured.sizeValues[i]}) on this tyre only.`}
                          >
                            ↺ Reset to tester value ({originalMccCaptured.sizeValues[i]})
                          </button>
                        )}
                    </div>
                  </td>
                  <td className="py-1">
                    <div className="flex flex-col gap-1">
                      <input
                      type="text"
                      value={brandRaw[p.id || `pt-${i}`] ?? brandModelValue}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setBrandRaw((prev) => ({
                          ...prev,
                          [p.id || `pt-${i}`]: raw,
                        }));
                      }}
                      onBlur={(e) => {
                        // iter193b — parse tokens & propagate on blur.
                        // Keeping the in-progress raw string in
                        // `brandRaw` and only splitting tokens here
                        // lets the user type "Michelin Primacy 4"
                        // including the spaces (the per-keystroke
                        // parse-and-rejoin in iter193 was stripping
                        // trailing whitespace, making spaces
                        // impossible to enter).
                        const brandModel = (e.target.value || "").trim();
                        const trimmedBase = baseLabel.trim();
                        const tokens = brandModel.split(/\s+/).filter(Boolean);
                        const brand = tokens[0] || null;
                        const model = tokens.length > 1 ? tokens.slice(1).join(" ") : null;
                        const combinedSpec = [sizeValue, brandModel].filter(Boolean).join(" ").trim();
                        setLocalParts((prev) => {
                          const next = [...prev];
                          next[i] = {
                            ...next[i],
                            tyre_brand: brand,
                            tyre_model: model,
                            tyre_spec:  combinedSpec || null,
                            description: combinedSpec
                              ? `${trimmedBase} — ${combinedSpec}`
                              : `${trimmedBase} (price TBC)`,
                          };
                          return next;
                        });
                        // Normalise the raw string so it reflects the
                        // parsed brand+model (drops double-spaces).
                        setBrandRaw((prev) => ({
                          ...prev,
                          [p.id || `pt-${i}`]: [brand, model].filter(Boolean).join(" "),
                        }));
                        // Mirror onto matching tyres child so the
                        // customer portal renders the same brand/model.
                        if (p.tyre_position || p.tyre_child_id) {
                          setLocalTyres((prevTyres) => prevTyres.map((t) => {
                            const matchByPos = p.tyre_position
                              && (t.position || "").toLowerCase()
                                 === p.tyre_position.toLowerCase();
                            const matchById  = p.tyre_child_id && t.id === p.tyre_child_id;
                            if (!matchByPos && !matchById) return t;
                            return { ...t, tyre_brand: brand, tyre_model: model };
                          }));
                        }
                        // Propagate to siblings (empty + non-locked).
                        if (brand) {
                          propagateOnBlur(i, "tyre_brand", brand, {
                            tyre_model: model,
                            _raw: brandModel,
                          });
                        }
                      }}
                      placeholder="e.g. Michelin Primacy 4"
                      className="w-full px-1.5 py-0.5 border border-slate-300 rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-400"
                      data-testid={`tyre-part-brand-model-${p.tyre_position || i}`}
                    />
                    {/* iter198i (Feb 2026) — Surface the tester's
                        observed damage chips directly under the
                        brand/model input, so FOH sees exactly what
                        the tester observed at the ramp ("Cord
                        exposed", "Tread below legal limit", …)
                        without having to flip back to the MCC card.
                        Backed by `mcc_damage_chips` from the
                        iter198g read-time enrichment. */}
                    {Array.isArray(p.mcc_damage_chips) && p.mcc_damage_chips.length > 0 && (
                      <div
                        className="flex flex-wrap gap-1"
                        data-testid={`tyre-part-mcc-damage-${p.tyre_position || i}`}
                      >
                        <span className="text-[9.5px] font-semibold uppercase tracking-wide text-slate-500 pt-0.5">
                          Tester:
                        </span>
                        {p.mcc_damage_chips.map((chip, ci) => (
                          <span
                            key={`${p.id || i}-chip-${ci}`}
                            className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-900 text-[10px] font-medium"
                            title="Captured by the tester via MCC rapid capture"
                          >
                            {chip}
                          </span>
                        ))}
                      </div>
                    )}
                    </div>
                  </td>
                  <td className="text-right py-1 text-slate-800">{qty}</td>
                  <td className="text-right py-1">
                    <PriceCell
                      value={p.buy_in_price}
                      onChange={(v) => {
                        const next = [...localParts];
                        next[i] = { ...p, buy_in_price: v };
                        setLocalParts(next);
                      }}
                      onBlur={() => propagatePriceOnBlur(i, "buy_in_price")}
                      testId={`tyre-part-buyin-${p.tyre_position || i}`}
                    />
                  </td>
                  <td className="text-right py-1">
                    <PriceCell
                      value={p.unit_price}
                      onChange={(v) => {
                        const next = [...localParts];
                        next[i] = { ...p, unit_price: v };
                        setLocalParts(next);
                      }}
                      onBlur={() => propagatePriceOnBlur(i, "unit_price")}
                      testId={`tyre-part-retail-${p.tyre_position || i}`}
                    />
                  </td>
                  <td
                    className={`text-right py-1 text-[11.5px] font-semibold ${
                      marginPct >= 40 ? "text-emerald-700"
                      : marginPct >= 20 ? "text-amber-700"
                      : "text-red-600"
                    }`}
                    data-testid={`tyre-part-margin-${p.tyre_position || i}`}
                  >
                    {retail > 0 ? `${marginPct.toFixed(0)}%` : "—"}
                  </td>
                  <td className="text-right py-1 font-semibold text-slate-900">
                    £{total.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Included items (charges) */}
        <table className="w-full text-[12px]" data-testid="tyre-group-charges-table">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left font-semibold py-1">Included with Replacement</th>
              <th className="text-right font-semibold py-1 w-20">Amount</th>
            </tr>
          </thead>
          <tbody>
            {localCharges.map((c, i) => (
              <tr key={c.id || `chg-${i}`} className="border-t border-slate-100" data-testid={`tyre-charge-row-${c.dedupe_key || i}`}>
                <td className="py-1 text-slate-800 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                  <span>{c.description}</span>
                  {c.locked && (
                    <span
                      className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-600 border border-slate-200"
                      title="Locked — included on every tyre fit and cannot be removed."
                      data-testid={`tyre-charge-locked-${c.dedupe_key || i}`}
                    >
                      <Lock className="w-2.5 h-2.5" />
                      Locked
                    </span>
                  )}
                </td>
                <td className="text-right py-1 font-semibold text-slate-900">
                  {editing ? (
                    <PriceCell value={c.amount} onChange={(v) => {
                      const next = [...localCharges];
                      next[i] = { ...c, amount: v };
                      setLocalCharges(next);
                    }} testId={`charge-amount-${i}`} />
                  ) : (
                    <span>£{(c.amount ?? 0).toFixed(2)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {editing && (
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={async () => {
                try {
                  await axios.put(`${API}/foh/yestimate/${yestimateId}`, {
                    job_groups: [{
                      ...jobGroup,
                      labour_lines: localLabour,
                      parts_lines:  localParts,
                      charges:      localCharges,
                      tyres:        localTyres,
                    }],
                  });
                  toast.success("Tyre group prices saved.");
                  setEditing(false);
                  onRefresh?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Save failed");
                }
              }}
              className="text-[11.5px] font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded px-3 py-1"
              data-testid="tyre-group-save-edits"
            >
              Save changes
            </button>
          </div>
        )}
      </div>

      {/* Recommendations */}
      {(alignmentSuggest?.warranted || axlePair) && (
        <div
          className="px-4 py-2.5 border-t border-slate-200 bg-blue-50/40 space-y-2"
          data-testid="tyre-group-recommendations"
        >
          <p className="text-[10.5px] uppercase tracking-wider text-blue-800 font-semibold">
            Recommended additional service
          </p>
          {alignmentSuggest?.warranted && (
            <button
              type="button"
              disabled={savingAlignment}
              onClick={handleAddAlignment}
              className="w-full text-left rounded border border-blue-300 bg-white px-3 py-2 hover:bg-blue-50 flex items-start gap-2 disabled:opacity-60"
              data-testid="tyre-group-add-alignment-cta"
            >
              {savingAlignment ? (
                <Loader2 className="w-4 h-4 text-blue-700 mt-0.5 shrink-0 animate-spin" />
              ) : (
                <MoveRight className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold text-blue-900">
                  {savingAlignment ? "Adding wheel alignment…" : "Add Wheel Alignment"}
                </p>
                <p className="text-[11.5px] text-slate-700 italic leading-snug">
                  {alignmentSuggest.reason}
                </p>
                {alignmentSuggest.recommended_alignment_type && (
                  <p className="text-[10.5px] text-slate-500 mt-0.5 uppercase tracking-wider">
                    Suggested type: {alignmentSuggest.recommended_alignment_type.replace(/_/g, " ")}
                  </p>
                )}
              </div>
            </button>
          )}
          {axlePair && (
            <button
              type="button"
              onClick={() => onAddAxlePair?.(axlePair, jobGroup)}
              className="w-full text-left rounded border border-slate-300 bg-white px-3 py-2 hover:bg-slate-50 flex items-start gap-2"
              data-testid="tyre-group-axle-pair-cta"
            >
              <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold text-slate-900">
                  {axlePair.label}
                </p>
                <p className="text-[11.5px] text-slate-700 italic leading-snug">
                  {axlePair.rationale}
                </p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* iter197p — Customer Advice Engine: per-JG primary narrative.
          Mounted inside the tyre-group card because the bespoke
          TyreReplacementGroupCard render path bypasses the standard
          CustomerCommunicationCard where CAE lives by default. */}
      <div className="px-4 pb-3">
        <CAESection
          jobGroup={jobGroup}
          yestimateId={yestimateId}
          onApplied={onRefresh}
        />
      </div>

      {/* Pricing footer */}
      <footer className="px-4 py-2 border-t border-slate-200 bg-white/70 flex items-center justify-between text-[12.5px]">
        <div className="flex items-center gap-2 text-slate-600">
          <Wrench className="w-3.5 h-3.5" />
          <span>
            {tyres.filter((t) => t.replacement_required).length} tyre
            {(tyres.filter((t) => t.replacement_required).length === 1) ? "" : "s"} to replace
            {(groupedLabour.total_hours ?? 0) > 0 && (
              <> · Labour {groupedLabour.total_hours}h</>
            )}
          </span>
        </div>
        <div className="text-right">
          {(() => {
            // iter196 — Live total computation. The BE's
            // ``jobGroup.total_inc_vat`` is stale until FOH saves and
            // the engine re-rolls. When FOH is mid-typing retail
            // prices on localParts, this footer would still show
            // the £0-tyre baseline (e.g. £67.20 charges-only on a
            // 4-tyre group at £60.95 retail each, where the real
            // total inc VAT is £359.76).
            //
            // We therefore compute the footer from local state:
            //   parts   = Σ qty × unit_price
            //   labour  = Σ hours × rate  (empty under current policy)
            //   charges = Σ amount
            //   subtotal = parts + labour + charges
            //   total inc VAT = subtotal × 1.20
            //
            // VAT rate matches the engine constant (20%). When the
            // BE has already persisted the prices, the math returns
            // the same number — so this never drifts from the BE.
            const VAT = 0.20;
            const partsSubtotal = (localParts || []).reduce(
              (acc, p) => acc + (parseFloat(p.quantity ?? 1) || 1) * (parseFloat(p.unit_price) || 0),
              0,
            );
            const labourSubtotal = (localLabour || []).reduce(
              (acc, l) => acc + (parseFloat(l.hours) || 0) * (parseFloat(l.rate) || 0),
              0,
            );
            const chargesSubtotal = (localCharges || []).reduce(
              (acc, c) => acc + (parseFloat(c.amount) || 0),
              0,
            );
            const subtotal = partsSubtotal + labourSubtotal + chargesSubtotal;
            const liveTotalInc = subtotal * (1 + VAT);
            const allUnpriced = (localParts || []).length > 0
              && (localParts || []).every((p) => !(parseFloat(p.unit_price) > 0));
            return (
              <>
                <p
                  className="font-bold text-slate-900"
                  data-testid="tyre-group-live-total"
                >
                  £{liveTotalInc.toFixed(2)}
                  <span className="text-[10.5px] text-slate-500 font-normal ml-1">inc VAT</span>
                </p>
                {allUnpriced && (
                  <p
                    className="text-[10.5px] text-red-600 font-semibold mt-0.5"
                    data-testid="tyre-group-footer-unpriced-note"
                  >
                    Tyres not yet priced — total is charges only
                  </p>
                )}
                {!allUnpriced && (
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Parts £{partsSubtotal.toFixed(2)} + Charges £{chargesSubtotal.toFixed(2)} + VAT £{(subtotal * VAT).toFixed(2)}
                  </p>
                )}
              </>
            );
          })()}
        </div>
      </footer>

      {/* Feb 2026 — Confirm Pricing footer. Mirrors the standard
          JobGroupCard footer so a tyre group can be marked priced
          and counted in the quote-progress meter. Without this the
          tyre group stayed "Pending" forever and FOH couldn't reach
          100% pricing complete.

          iter195 — Per-tyre price completeness gate. Any in-quote
          tyre with a £0 retail blocks the Confirm button so FOH
          can't accidentally send a £0 tyre quote to a customer. */}
      {(onConfirmPricing || onUnconfirmPricing) && (() => {
        const unpricedTyres = (localParts || []).filter(
          (p) => !(parseFloat(p.unit_price) > 0),
        );
        const hasUnpriced = unpricedTyres.length > 0;
        return (
          <div className="px-4 py-3 border-t border-slate-200">
            {jobGroup.pricing_confirmed ? (
              <div
                className="flex items-center justify-between"
                data-testid={`tyre-group-pricing-confirmed-${jobGroup.id}`}
              >
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Pricing Confirmed</span>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    // iter211as — Persist the unconfirm so a reload
                    // doesn't restore the previous "confirmed" state.
                    try {
                      if (yestimateId) {
                        await axios.put(`${API}/foh/yestimate/${yestimateId}`, {
                          job_groups: [{
                            ...jobGroup,
                            pricing_confirmed: false,
                          }],
                        });
                      }
                    } catch { /* non-blocking */ }
                    onUnconfirmPricing?.(jobGroup.id);
                  }}
                  className="text-xs text-slate-500 hover:text-slate-800 underline"
                  data-testid={`tyre-group-pricing-edit-${jobGroup.id}`}
                >
                  Edit
                </button>
              </div>
            ) : (
              <>
                {hasUnpriced && (
                  <div
                    className="mb-2 flex items-start gap-2 px-3 py-2 rounded-md border border-red-300 bg-red-50 text-[12.5px] text-red-800"
                    data-testid={`tyre-group-unpriced-warning-${jobGroup.id}`}
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      <strong>{unpricedTyres.length}</strong> tyre
                      {unpricedTyres.length === 1 ? "" : "s"} still need a retail price
                      ({unpricedTyres.map((p) => p.tyre_position || "?").join(", ")}).
                      Pricing cannot be confirmed until all in-quote tyres have a price.
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    if (hasUnpriced) return;
                    // iter195 — persist localParts/Tyres/Charges before
                    // marking the JG priced. Without this, prices the
                    // FOH typed (and propagated to siblings) live in
                    // localParts state and would be lost on next
                    // refresh — leaving the JG flagged "priced" but
                    // still carrying £0 retails.
                    // iter211as — LK61JFF bug: also persist
                    // `pricing_confirmed: true` so the left-panel
                    // "green when priced" state survives a reload.
                    // Without this, only the local App state saw the
                    // confirm; a reload restored the stored value
                    // (false) and the left rail went amber again.
                    try {
                      if (yestimateId) {
                        await axios.put(`${API}/foh/yestimate/${yestimateId}`, {
                          job_groups: [{
                            ...jobGroup,
                            labour_lines:      localLabour,
                            parts_lines:       localParts,
                            charges:           localCharges,
                            tyres:             localTyres,
                            pricing_confirmed: true,
                          }],
                        });
                      }
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not save tyre prices — please try again.");
                      return;
                    }
                    onConfirmPricing?.(jobGroup.id);
                  }}
                  disabled={hasUnpriced}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                    hasUnpriced
                      ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                      : "bg-amber-500 hover:bg-amber-600 text-white"
                  }`}
                  data-testid={`confirm-pricing-${jobGroup.id}`}
                  title={
                    hasUnpriced
                      ? "Enter a retail price on every in-quote tyre first."
                      : ""
                  }
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {hasUnpriced
                    ? "Enter retail prices first"
                    : "Confirm Pricing Complete"}
                </button>
              </>
            )}
          </div>
        );
      })()}
    </section>
  );
}
